/**
 * TIP Shell — the BRIDGE (trusted parent ↔ sealed frame).
 *
 * Graduates the Phase-0 spike (tip-spike/shell/shell.js) to TypeScript and upgrades
 * the transport to the iframe-initiated MessageChannel PORT handshake + per-launch
 * nonce (SCAFFOLD §4). The spike's VALIDATED invariants are kept intact:
 *
 *   • Frame identity is bound by `event.source === iframe.contentWindow` (the strong,
 *     reliable check) — NOT by event.origin, which is the opaque "null" for a
 *     sandbox="allow-scripts" frame.
 *   • The opaque origin forces any window-level outbound send to targetOrigin '*'
 *     (see seal.ts FRAME_TARGET). Post-handshake we use the MessagePort (origin-agnostic).
 *   • `tip:init` is sent EXACTLY ONCE. A second frame `load` = self-nav = TRIPWIRE.
 *   • high_assurance NEVER lets a plaintext name cross into the frame: names are
 *     painted into PARENT-DOM trusted-chrome slots on `tip:renderName`, and `tip:resolve`
 *     is answered tags-only.
 *
 * The Bridge is framework-agnostic (no Preact): it talks to the UI through `BridgeHost`.
 */
import type { Tier, IdentitySlot, BrokerMethod, FrameRuntimeReport, SlotRect } from './protocol.js';
import { type FramePortHandshake, type ShellToFrame, type BridgeFromFrame, BROKER_METHODS } from './protocol.js';
import type { LeaderboardRow, ResolveResult, LeaderboardResult, BrokerResult } from './api.js';
import { Tripwire } from './tripwire.js';
import { scrubNames } from './scrub.js';
import { parseSlotRect } from './name-overlay.js';

/**
 * The FOUR calls the Bridge and its Tripwire make. Structural rather than the concrete
 * `ServerClient` so a TEST RUN can supply a stand-in that never touches the network:
 * a teacher previewing an unpublished draft has no app id to resolve, no roster to
 * resolve it against, and nothing to quarantine, so the honest implementation is a local
 * one — not a real client pointed at a synthetic app id.
 *
 * ServerClient satisfies this by construction; widening it does not loosen anything on
 * the student path, which still receives the real client.
 */
export interface BridgeApi {
  resolveIdentity(tags: string[]): Promise<ResolveResult>;
  resolveLeaderboard(
    appId: string,
    scope: { classId: string; assignmentId: string },
  ): Promise<LeaderboardResult>;
  broker(reqId: string, method: BrokerMethod, args: unknown, appId: string): Promise<BrokerResult>;
  adminKill(
    appId: string, reason: string,
    source?: 'admin' | 'egress_tripwire', detectedAt?: string,
  ): Promise<boolean>;
}

export interface BridgeHost {
  log(kind: 'out' | 'in' | 'warn' | 'info' | 'breach', msg: string): void;
  /** Paint a resolved name. `tag` is the tag the slot is BOUND to; a host must position
   *  (overlay) a name only when `tag` is the viewer's own. */
  paintSlot(slotId: string, tag: string, name: string): void;
  /** Where the app's anchor for a PAINTED name slot is (frame-viewport px), or null when
   *  it has left the document. Already shape-validated and bound to a slot this Bridge
   *  painted; the host still CLAMPS it to the iframe's box before painting (name-overlay.ts). */
  placeSlot(slotId: string, rect: SlotRect | null): void;
  /** Paint the trusted-chrome leaderboard (parent DOM). Rows carry names + scores; the
   *  host renders them and they are NEVER posted back into the frame. */
  paintLeaderboard(slotId: string, rows: LeaderboardRow[]): void;
  setStatus(s: string): void;
  onBreach(reason: string, killed: boolean): void;
}

export interface BridgeOptions {
  iframe: HTMLIFrameElement;
  sandboxOrigin: string;
  expectedNonce: string;
  tier: Tier; // COMPUTED tier — authoritative, never app-declared
  // Whether the GRANT's computed exposed_fields include first_name. Name delivery into
  // the frame is gated on THIS (the law), not on tier alone — so a standard-tier app that
  // requested no fields, or an opted-out viewer, gets a name-blind frame. (exposed_fields
  // is already empty for high_assurance, so this subsumes the tier check for names.)
  mayExposeFirstName: boolean;
  appId: string;
  self: { tag: string };
  roster: { tag: string }[];
  context?: import('../shared/sdk.js').TipActivityContext; // Shell-injected activity scope (opaque, non-PII)
  /** The school's look (migration 0016) — rides in BOTH tiers: a school name, a mascot
   *  and two colours are not about any student. */
  theme?: { schoolName: string; mascot: string; primary: string; accent: string };
  api: BridgeApi;
  host: BridgeHost;
}

/** Name slots (tip:renderName + tip:slotRect). The frame is untrusted and both messages are
 *  flood surfaces, so everything is bounded BEFORE any name is resolved or painted:
 *  a slot id is a short identifier, a tag is bounded, a frame may bind at most
 *  MAX_NAME_SLOTS slots in its lifetime — split so that classmate slots can never use up
 *  the viewer's own (review round 2: eight classmate bindings used to lock the viewer's
 *  name out) — renderName is rate-limited, and rects are rate-limited and COALESCED. */
export const SLOT_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const MAX_TAG_CHARS = 128;
export const MAX_SELF_NAME_SLOTS = 4;
export const MAX_OTHER_NAME_SLOTS = 4;
export const MAX_NAME_SLOTS = MAX_SELF_NAME_SLOTS + MAX_OTHER_NAME_SLOTS;
const MAX_RENDER_NAME_PER_SEC = 20;
const MAX_SLOT_RECT_MSGS_PER_SEC = 240;
const SLOT_RECT_COALESCE_MS = 16;

/** A name slot is BOUND to the first tag it was rendered with, for the frame's lifetime.
 *  `gen` identifies the latest renderName for it; a resolution that finishes after a newer
 *  one started, or for a binding that is no longer current, is discarded unpainted. */
interface NameSlotBinding { tag: string; gen: number; painted: boolean }

/** Bounds on a frame-reported test run. The frame is untrusted; its report is data. */
const MAX_REPORT_ERRORS = 12;
const MAX_REPORT_ERROR_CHARS = 300;

/**
 * Normalize a frame-reported run into the shape the console will render.
 *
 * The SDK bounds this already, but the SDK runs INSIDE the sealed frame — so it is code
 * we wrote executing in a place we do not control, and its output is re-bounded here on
 * the trusted side. The strings are rendered as text by Preact and never interpolated
 * into markup; these caps are about size, not escaping.
 */
function sanitizeReport(raw: unknown): FrameRuntimeReport {
  const r = (raw ?? {}) as Record<string, unknown>;
  const errs = Array.isArray(r['errors']) ? r['errors'] : [];
  return {
    errors: errs
      .filter((e): e is string => typeof e === 'string')
      .slice(0, MAX_REPORT_ERRORS)
      .map((e) => e.slice(0, MAX_REPORT_ERROR_CHARS)),
    rendered: Number.isFinite(r['rendered']) ? Math.max(0, Math.trunc(r['rendered'] as number)) : 0,
    wroteScore: r['wroteScore'] === true,
    ready: r['ready'] === true,
  };
}

export class Bridge {
  private readonly o: BridgeOptions;
  private readonly acceptedOrigins: Set<string>;
  private readonly tripwire: Tripwire;
  private readonly nameCache = new Map<string, string | null>();

  private port: MessagePort | null = null;
  private portBound = false;
  private initSent = false;
  private loadCount = 0;
  private disposed = false;
  /** In-flight test-run probes, by id. Diagnostics only — never on a student path. */
  private readonly probes = new Map<string, (r: FrameRuntimeReport | null) => void>();
  /** renderName slots, each bound to ONE tag for the frame's lifetime (never rebound).
   *  A tip:slotRect is honoured ONLY for a painted slot bound to the viewer's own tag. */
  private readonly nameSlots = new Map<string, NameSlotBinding>();
  private nameWindowStart = 0;
  private nameWindowCount = 0;
  private nameWarned = false;
  /** Latest rect per slot, awaiting the coalesced flush (or the slot's paint). */
  private readonly pendingRects = new Map<string, SlotRect | null>();
  private rectFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private rectWindowStart = 0;
  private rectWindowCount = 0;
  private rectWarned = false;

  private readonly onWindowMessage = (e: MessageEvent): void => this.handleWindowMessage(e);
  private readonly onFrameLoad = (): void => this.handleFrameLoad();

  constructor(o: BridgeOptions) {
    this.o = o;
    // Opaque frame ⇒ inbound origin is "null"; SANDBOX_ORIGIN kept as a fallback in
    // case a future config ever adds allow-same-origin (spike note).
    this.acceptedOrigins = new Set<string>(['null', o.sandboxOrigin]);
    this.tripwire = new Tripwire({
      appId: o.appId,
      api: o.api,
      onBreach: (reason, killed) => o.host.onBreach(reason, killed),
      log: (k, m) => o.host.log(k, m),
    });
  }

  /** Names may cross into the frame ONLY when the computed grant exposes first_name AND the
   *  tier is standard (high_assurance already exposes nothing; this gates on the law, not
   *  tier alone). Governs `tip:init` and the `tip:resolve` echo. */
  private get showNames(): boolean {
    return this.o.tier === 'standard' && this.o.mayExposeFirstName;
  }

  /** Arm the Bridge: listen for the port handshake + the second-load tripwire. */
  start(): void {
    window.addEventListener('message', this.onWindowMessage);
    this.o.iframe.addEventListener('load', this.onFrameLoad);
    this.o.host.log(
      'info',
      `bridge armed — awaiting port handshake (nonce=${this.o.expectedNonce.slice(0, 8)}…, tier=${this.o.tier})`,
    );
    // Fail-closed: if no handshake arrives, the frame simply never receives identity.
    window.setTimeout(() => {
      if (!this.portBound && !this.disposed) {
        this.o.host.log('warn', 'no tip:port handshake within 4s — frame received NO identity (fail-closed)');
      }
    }, 4000);
  }

  dispose(): void {
    this.disposed = true;
    if (this.rectFlushTimer !== null) clearTimeout(this.rectFlushTimer);
    this.rectFlushTimer = null;
    this.pendingRects.clear();
    window.removeEventListener('message', this.onWindowMessage);
    this.o.iframe.removeEventListener('load', this.onFrameLoad);
    if (this.port) {
      this.port.onmessage = null;
      try {
        this.port.close();
      } catch {
        /* noop */
      }
    }
  }

  /**
   * Ask the frame how its run went — the TEST RUN a teacher does before publishing.
   *
   * Resolves null if the frame never bound its port or never answers, which is itself
   * the finding ("it did not start"). The report is DIAGNOSTIC: it is shown to the
   * teacher and recorded as evidence that a human ran the app. It authorizes nothing,
   * and no student path calls this.
   */
  probe(timeoutMs = 3_000): Promise<FrameRuntimeReport | null> {
    if (!this.portBound || !this.port) return Promise.resolve(null);
    const probeId = crypto.randomUUID();
    return new Promise<FrameRuntimeReport | null>((resolve) => {
      const done = (r: FrameRuntimeReport | null): void => {
        if (!this.probes.delete(probeId)) return; // already settled
        window.clearTimeout(timer);
        resolve(r);
      };
      const timer = window.setTimeout(() => done(null), timeoutMs);
      this.probes.set(probeId, done);
      const msg: ShellToFrame = { type: 'tip:probe', probeId };
      this.port?.postMessage(msg);
    });
  }

  /** Input mediation (§4): standard forwards keystrokes; high_assurance is frame-blind. */
  forwardInput(value: string): void {
    if (this.o.tier === 'high_assurance') {
      this.o.host.log('info', `input keystroke captured in chrome, NOT forwarded (len=${value.length})`);
      return;
    }
    if (!this.portBound || !this.port) return;
    const msg: ShellToFrame = { type: 'tip:input', value };
    this.port.postMessage(msg);
    this.o.host.log('out', `tip:input forwarded to frame (len=${value.length})`);
  }

  // ── Second-load tripwire ───────────────────────────────────────────────────
  private handleFrameLoad(): void {
    this.loadCount += 1;
    if (this.loadCount === 1) {
      this.o.host.log('info', 'frame first load — legitimate');
      return;
    }
    // A SECOND load event = the frame document was replaced = self-navigation (KC-2).
    this.o.host.log('breach', `frame emitted load #${this.loadCount} — SELF-NAV DETECTED`);
    this.tripwire.trip('second_load_self_nav');
  }

  // ── Port handshake (window level, pre-port only) ────────────────────────────
  private handleWindowMessage(e: MessageEvent): void {
    if (this.disposed) return;
    // Strong identity bind FIRST: must be the frame WE created.
    if (!this.o.iframe || e.source !== this.o.iframe.contentWindow) return;
    if (!this.acceptedOrigins.has(e.origin)) {
      this.o.host.log('warn', `REJECTED window message — origin="${e.origin}" not accepted`);
      return;
    }
    const d = e.data as unknown;
    if (!d || typeof d !== 'object') return;
    const type = (d as { type?: unknown }).type;
    if (type !== 'tip:port') {
      // Post-handshake we accept ONLY port messages; a window-level app message is anomalous.
      this.o.host.log('warn', `ignoring window-level message type="${String(type)}" (the channel is the port)`);
      return;
    }
    this.bindPort(d as FramePortHandshake, e);
  }

  private bindPort(msg: FramePortHandshake, e: MessageEvent): void {
    if (this.portBound) {
      this.o.host.log('warn', 'duplicate tip:port ignored (port already bound)');
      return;
    }
    if (msg.nonce !== this.o.expectedNonce) {
      this.o.host.log('warn', 'tip:port REJECTED — nonce mismatch (not this launch)');
      return;
    }
    const port = e.ports && e.ports[0];
    if (!port) {
      this.o.host.log('warn', 'tip:port carried no MessagePort — ignored');
      return;
    }
    this.port = port;
    this.portBound = true;
    port.onmessage = (ev: MessageEvent): void => this.handlePortMessage(ev);
    try {
      port.start();
    } catch {
      /* onmessage assignment auto-starts the port */
    }
    this.o.host.log('in', 'tip:port bound — nonce ok, event.source===frame; channel established');
    this.o.host.setStatus(`port handshake complete — tier=${this.o.tier}`);
    void this.sendInit();
  }

  // ── tip:init (exactly once) ─────────────────────────────────────────────────
  private async sendInit(): Promise<void> {
    if (this.initSent || !this.port) return;
    const { tier, self, roster } = this.o;
    if (this.o.context?.kind === 'resource') {
      this.port.postMessage({type:'tip:init',tier,self:{tag:'resource-viewer'},roster:[],context:this.o.context,...(this.o.theme ? {theme:this.o.theme} : {})});
      this.initSent=true; return;
    }

    // Activity scope (classId/assignmentId) rides in BOTH tiers — the ref is opaque and
    // non-PII, and the app needs an assignmentId to writeScore against.
    const ctx = {
      ...(this.o.context ? { context: this.o.context } : {}),
      ...(this.o.theme ? { theme: this.o.theme } : {}),
    };

    let payload: ShellToFrame;
    if (!this.showNames) {
      // TAGS ONLY — high_assurance, OR standard with no first_name exposed (app requested
      // none / viewer opted out). Names, if any, are painted in the parent DOM on request.
      payload = {
        type: 'tip:init',
        tier,
        self: { tag: self.tag },
        roster: roster.map((r) => ({ tag: r.tag })),
        ...ctx,
      };
    } else {
      // The grant exposes first_name (standard): the frame legitimately holds own +
      // same-class FIRST NAMES.
      const tags = [self.tag, ...roster.map((r) => r.tag)];
      const names = await this.resolveNames(tags);
      const slot = (tag: string): IdentitySlot => {
        const g = names.get(tag);
        return g == null ? { tag } : { tag, given: g };
      };
      payload = { type: 'tip:init', tier, self: slot(self.tag), roster: roster.map((r) => slot(r.tag)), ...ctx };
    }

    if (this.initSent) return; // re-check after the await — send exactly once
    this.port.postMessage(payload);
    this.initSent = true;
    this.o.host.log(
      'out',
      this.showNames
        ? 'tip:init sent — first names included (grant exposes first_name)'
        : 'tip:init sent — TAGS ONLY (grant exposes no name; painted in parent DOM on request)',
    );
  }

  // ── Inbound port messages (the frame's only channel) ────────────────────────
  private handlePortMessage(ev: MessageEvent): void {
    const d = ev.data as unknown;
    if (!d || typeof d !== 'object') return;
    const m = d as BridgeFromFrame;
    switch (m.type) {
      case 'tip:renderName':
        void this.handleRenderName(m);
        return;
      case 'tip:slotRect':
        this.handleSlotRect(m);
        return;
      case 'tip:renderLeaderboard':
        if (this.o.context?.kind === 'resource') return;
        void this.handleRenderLeaderboard(m);
        return;
      case 'tip:broker':
        void this.handleBroker(m);
        return;
      case 'tip:resolve':
        void this.handleResolve(m);
        return;
      case 'tip:probeResult': {
        // Only a probe WE issued is honoured; an unsolicited or duplicate id is dropped.
        const settle = this.probes.get(m.probeId);
        if (settle) settle(sanitizeReport(m.report));
        return;
      }
      default: {
        // tip:input is a Shell→frame message; a frame emitting it is anomalous.
        const t = (m as { type?: unknown }).type;
        if (t === 'tip:input') {
          this.o.host.log('warn', 'frame sent tip:input (Shell→frame only) — ignored');
        } else {
          this.o.host.log('warn', `unhandled port message type="${String(t)}"`);
        }
      }
    }
  }

  /** high_assurance: resolve the name server-side and paint it in the PARENT DOM.
   *  The name is NEVER posted back into the frame.
   *
   *  SLOT BINDING (the stricter of the two possible rules): a slot is bound to the tag of
   *  the FIRST renderName that names it, for the life of the frame. A later renderName for
   *  that slot with ANY other tag is refused outright — it is not treated as a fresh slot.
   *  After the await, the result is painted only if the slot is still bound to this tag and
   *  this is still the latest request for it (generation). This is what closes the race
   *  where a slow classmate lookup landed on a slot the viewer's own lookup had admitted.
   *
   *  POSITIONED ONLY FOR THE VIEWER'S OWN NAME. A positioned slot puts a real name next to
   *  content the app chose. For the viewer that is a greeting; for a classmate it would be
   *  a forgery kit ("Jordan — 2/10" laid out by the app with a real name it never held),
   *  the name↔value pairing renderLeaderboard exists to deny. So a classmate's slot still
   *  paints in the strip, outside the frame, but never accepts geometry. */
  private async handleRenderName(m: Extract<BridgeFromFrame, { type: 'tip:renderName' }>): Promise<void> {
    if (this.o.context?.kind === 'resource') return;
    // Rate bound FIRST, before any validation work or a registrar round-trip.
    const now = Date.now();
    if (now - this.nameWindowStart >= 1000) {
      this.nameWindowStart = now;
      this.nameWindowCount = 0;
      this.nameWarned = false;
    }
    if (++this.nameWindowCount > MAX_RENDER_NAME_PER_SEC) {
      this.warnNameOnce(`tip:renderName flood — over ${MAX_RENDER_NAME_PER_SEC}/s, dropping`);
      return;
    }
    const raw = m as unknown as Record<string, unknown>;
    const tag = raw['tag'];
    const slotId = raw['slotId'];
    if (typeof tag !== 'string' || typeof slotId !== 'string') {
      this.warnNameOnce('tip:renderName ignored — tag/slotId must be strings');
      return;
    }
    // Validated BEFORE it is used as a key or written to the log.
    if (!SLOT_ID_RE.test(slotId)) {
      this.warnNameOnce('tip:renderName ignored — slotId must match [A-Za-z0-9_-]{1,64}');
      return;
    }
    if (tag.length === 0 || tag.length > MAX_TAG_CHARS) {
      this.warnNameOnce(`tip:renderName ignored — tag must be 1..${MAX_TAG_CHARS} chars`);
      return;
    }
    let b = this.nameSlots.get(slotId);
    if (b && b.tag !== tag) {
      this.o.host.log('warn', `tip:renderName refused — slot "${slotId}" is bound to another tag; a slot is never rebound`);
      return;
    }
    if (!b) {
      const isSelf = tag === this.o.self.tag;
      let same = 0;
      for (const x of this.nameSlots.values()) if ((x.tag === this.o.self.tag) === isSelf) same += 1;
      const cap = isSelf ? MAX_SELF_NAME_SLOTS : MAX_OTHER_NAME_SLOTS;
      if (same >= cap) {
        this.warnNameOnce(`tip:renderName refused — this frame already has ${cap} ${isSelf ? "of the viewer's" : 'classmate'} name slots`);
        return;
      }
      b = { tag, gen: 0, painted: false };
      this.nameSlots.set(slotId, b);
    }
    const gen = ++b.gen;
    const binding = b;
    this.o.host.log('in', `tip:renderName tag=${tag} slotId=${slotId}`);

    const names = await this.resolveNames([tag]);
    // RE-CHECK after the await: same binding object, same tag, still the latest request.
    if (this.disposed) return;
    const cur = this.nameSlots.get(slotId);
    if (cur !== binding || cur.tag !== tag || cur.gen !== gen) return;

    const name = names.get(tag);
    if (name == null) {
      // The binding stays (the slot is still spent and still cannot be rebound).
      this.o.host.log('warn', `tip:renderName — unresolvable tag ${tag}, nothing painted`);
      return;
    }
    this.o.host.paintSlot(slotId, tag, name);
    binding.painted = true;
    this.o.host.log('info', `painted parent-DOM slot "${slotId}" for ${tag} — NOT sent to frame`);
    if (tag === this.o.self.tag && this.pendingRects.has(slotId)) this.scheduleRectFlush();
  }

  /**
   * The app's anchor geometry for a name slot, reported by the SDK. Accepted ONLY on the
   * capability port (this is only reachable from handlePortMessage), ONLY for a slot bound
   * to the viewer's OWN tag, and ONLY with a fully well-formed rect — anything else is
   * dropped whole. It is geometry the frame supplies about its OWN document, so the worst
   * a hostile frame can do is misplace the viewer's name inside its own box; the host's
   * clamp enforces that box.
   */
  private handleSlotRect(m: Extract<BridgeFromFrame, { type: 'tip:slotRect' }>): void {
    // Rate bound FIRST, so a flood costs a counter increment and nothing else.
    const now = Date.now();
    if (now - this.rectWindowStart >= 1000) {
      this.rectWindowStart = now;
      this.rectWindowCount = 0;
      this.rectWarned = false;
    }
    if (++this.rectWindowCount > MAX_SLOT_RECT_MSGS_PER_SEC) {
      this.warnRectOnce(`tip:slotRect flood — over ${MAX_SLOT_RECT_MSGS_PER_SEC}/s, dropping`);
      return;
    }
    const raw = m as unknown as Record<string, unknown>;
    const slotId = raw['slotId'];
    if (typeof slotId !== 'string' || !SLOT_ID_RE.test(slotId)) {
      this.warnRectOnce('tip:slotRect ignored — slotId must match [A-Za-z0-9_-]{1,64}');
      return;
    }
    const rect = parseSlotRect(raw['rect']);
    if (rect === undefined) {
      this.warnRectOnce(`tip:slotRect ignored — malformed rect for slot "${slotId}"`);
      return;
    }
    const b = this.nameSlots.get(slotId);
    if (!b || b.tag !== this.o.self.tag) {
      this.warnRectOnce(`tip:slotRect ignored — "${slotId}" is not a name slot bound to the viewer`);
      return;
    }
    this.pendingRects.set(slotId, rect); // latest wins (coalesced)
    if (b.painted) this.scheduleRectFlush();
  }

  private scheduleRectFlush(): void {
    if (this.rectFlushTimer !== null || this.disposed) return;
    this.rectFlushTimer = setTimeout(() => this.flushRects(), SLOT_RECT_COALESCE_MS);
  }

  /** Apply the latest rect for every PAINTED slot. Pending slots keep theirs until painted. */
  private flushRects(): void {
    this.rectFlushTimer = null;
    if (this.disposed) return;
    for (const [slotId, rect] of this.pendingRects) {
      const b = this.nameSlots.get(slotId);
      if (!b || !b.painted || b.tag !== this.o.self.tag) continue;
      this.pendingRects.delete(slotId);
      this.o.host.placeSlot(slotId, rect);
    }
  }

  private warnNameOnce(msg: string): void {
    if (this.nameWarned) return;
    this.nameWarned = true;
    this.o.host.log('warn', msg);
  }

  private warnRectOnce(msg: string): void {
    if (this.rectWarned) return;
    this.rectWarned = true;
    this.o.host.log('warn', msg);
  }

  /** Scope-ref leaderboard. The frame supplies ONLY the scope + a slot; the Shell
   *  resolves broker-authoritative name+score rows and paints them in the PARENT DOM.
   *  The rows (which may carry names) are NEVER posted back into the frame — the app
   *  is structurally unable to pair a real name with a value. */
  private async handleRenderLeaderboard(
    m: Extract<BridgeFromFrame, { type: 'tip:renderLeaderboard' }>,
  ): Promise<void> {
    const { scope, slotId } = m;
    if (
      typeof slotId !== 'string' ||
      !scope || typeof scope !== 'object' ||
      typeof scope.classId !== 'string' || typeof scope.assignmentId !== 'string'
    ) {
      this.o.host.log('warn', 'tip:renderLeaderboard ignored — scope{classId,assignmentId}/slotId must be strings');
      return;
    }
    this.o.host.log('in', `tip:renderLeaderboard slotId=${slotId} scope=${scope.classId}/${scope.assignmentId}`);

    let res: LeaderboardResult;
    try {
      res = await this.o.api.resolveLeaderboard(this.o.appId, scope);
    } catch (e) {
      this.o.host.log('warn', `/leaderboard/resolve failed: ${String(e)} — nothing painted`);
      return;
    }
    if (res.deny || !res.rows) {
      this.o.host.log('warn', `leaderboard denied (fail-closed): ${res.reason ?? '(no reason)'} — nothing painted`);
      this.o.host.paintLeaderboard(slotId, []); // clear/empty, never a stale board
      return;
    }
    // Painted in the PARENT DOM (trusted chrome). Names, if present, never re-enter the frame.
    this.o.host.paintLeaderboard(slotId, res.rows);
    const named = res.rows.filter((r) => r.given != null).length;
    this.o.host.log(
      'info',
      `leaderboard painted in parent DOM — ${res.rows.length} row(s), ${named} name(s) — NOT sent to frame`,
    );
  }

  /** Door-1 broker proxy. Result is name-scrubbed before it crosses into the frame. */
  private async handleBroker(m: Extract<BridgeFromFrame, { type: 'tip:broker' }>): Promise<void> {
    if (this.o.context?.kind === 'resource') { this.replyBroker(m.reqId,{ok:false,error:'resource_local_only'}); return; }
    const { reqId, method, args } = m;
    if (typeof reqId !== 'string') {
      this.o.host.log('warn', 'tip:broker missing reqId — ignored');
      return;
    }
    this.o.host.log('in', `tip:broker reqId=${reqId} method=${String(method)}`);
    if (!BROKER_METHODS.has(method as BrokerMethod)) {
      this.replyBroker(reqId, { ok: false, error: `unknown method: ${String(method)}` });
      this.o.host.log('warn', `tip:broker rejected — unknown method ${String(method)}`);
      return;
    }
    const res = await this.o.api.broker(reqId, method as BrokerMethod, args, this.o.appId);
    if (res.ok) {
      // defense in depth: strip any name-like field before the payload enters the frame.
      this.replyBroker(reqId, { ok: true, data: scrubNames(res.data) });
    } else {
      this.replyBroker(reqId, { ok: false, error: res.error ?? 'broker error' });
    }
    this.o.host.log(res.ok ? 'out' : 'warn', `tip:brokerResult reqId=${reqId} ok=${res.ok}`);
  }

  private replyBroker(reqId: string, r: { ok: boolean; data?: unknown; error?: string }): void {
    if (!this.port) return;
    const msg: ShellToFrame = r.ok
      ? { type: 'tip:brokerResult', reqId, ok: true, data: r.data }
      : { type: 'tip:brokerResult', reqId, ok: false, error: r.error ?? 'broker error' };
    this.port.postMessage(msg);
  }

  /** Adversarial probe. high_assurance answers tags-only; standard may echo names (logged). */
  private async handleResolve(m: Extract<BridgeFromFrame, { type: 'tip:resolve' }>): Promise<void> {
    const tags = Array.isArray(m.tags) ? m.tags.filter((t): t is string => typeof t === 'string') : [];
    this.o.host.log('in', `tip:resolve tags=[${tags.join(', ')}]`);

    let resolved: { tag: string; given?: string }[];
    if (!this.showNames) {
      resolved = tags.map((tag) => ({ tag })); // REFUSE: tags only, never names (grant exposes none)
      this.o.host.log('warn', `tip:resolve REFUSED (grant exposes no name) — ${resolved.length} TAGS ONLY, zero names`);
    } else {
      const names = await this.resolveNames(tags);
      resolved = tags.map((tag) => {
        const g = names.get(tag);
        return g == null ? { tag } : { tag, given: g };
      });
      this.o.host.log('info', `tip:resolve — standard tier: echoing ${resolved.length} name(s) (allowed, logged)`);
    }
    if (!this.port) return;
    const reply: ShellToFrame = { type: 'tip:resolved', tags, resolved };
    this.port.postMessage(reply);
    this.o.host.log('out', `tip:resolved sent — ${this.o.tier === 'high_assurance' ? 'tags only' : 'names echoed'}`);
  }

  // ── Registrar-backed name resolution (cached) ───────────────────────────────
  private async resolveNames(tags: string[]): Promise<Map<string, string | null>> {
    const out = new Map<string, string | null>();
    const missing: string[] = [];
    for (const t of tags) {
      if (this.nameCache.has(t)) out.set(t, this.nameCache.get(t) ?? null);
      else missing.push(t);
    }
    if (missing.length) {
      let res: { resolved: { tag: string; given?: string }[] };
      try {
        res = await this.o.api.resolveIdentity(missing);
      } catch (e) {
        this.o.host.log('warn', `/identity/resolve failed: ${String(e)}`);
        res = { resolved: [] };
      }
      for (const t of missing) {
        this.nameCache.set(t, null);
        out.set(t, null);
      }
      for (const r of res.resolved) {
        const g = r.given ?? null;
        this.nameCache.set(r.tag, g);
        out.set(r.tag, g);
      }
    }
    return out;
  }
}
